import { Component, HostListener, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PortalMaterialModule } from '../../material.components.module';
import { MatSnackBar } from '@angular/material';
import { AuthDispatcher } from '../../store/auth/auth-dispatcher';
import { select } from '@angular-redux/store';
import { AuthenticationService } from '../../authentication/authentication.service';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Auth } from '../../store/auth/auth-state.model';
import { CommonDispatcher } from '../../store/common/common-dispatcher';
import { FinegrainRolesService } from '../../common/services/finegrain-roles/finegrain-roles.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [CommonModule, RouterModule, PortalMaterialModule, TranslateModule]
})
export class HeaderComponent implements OnInit, OnDestroy {
  /**
   *@arg to store if logged in or not
   **/
  public isLoggedIn: boolean;
  /**
   *@arg to store user name
   **/
  public userName: string;
  /**
   *@arg activePortal active portal avpp or admin
   **/
  public activePortal: string;
  /**
   *@arg adminCheck to store adminCheck
   **/
  public adminCheck: boolean;
  /**
   *@arg readCheck to store readCheck
   **/
  public readCheck = false;
  /**
   *@arg authObservable observe auth variable in redux
   **/
  @select('auth') readonly authObservable: Observable<Auth>;
  /**
   *@arg activePortals is is set of portal that where availabe for navigation
   **/
  private activePortals: string[];

  private idleSubscriptions: Subscription[] = [];

  private readonly IDLE_TIMEOUT_MS = 60 * 60 * 1000;
  private readonly IDLE_STORAGE_KEY = 'idle.lastActivityTime';
  private readonly LOGOUT_BROADCAST_KEY = 'app.logout';
  private idleCheckInterval: any = null;
  private boundActivityHandler: (_event: Event) => void;
  private visibilityChangeHandler: () => void;
  private storageLogoutHandler: ((e: StorageEvent) => void) | null = null;
  private readonly USER_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'touchmove', 'wheel'];

  private get lastActivityTime(): number {
    try {
      const stored = localStorage.getItem(this.IDLE_STORAGE_KEY);
      return stored ? parseInt(stored, 10) : Date.now();
    } catch (_) { return Date.now(); }
  }
  private set lastActivityTime(value: number) {
    try { localStorage.setItem(this.IDLE_STORAGE_KEY, String(value)); } catch (_) { }
  }
  private lastActivityTimeExists(): boolean {
    try { return !!localStorage.getItem(this.IDLE_STORAGE_KEY); } catch (_) { return false; }
  }

  constructor(
    public router: Router,
    public authDispatcher: AuthDispatcher,
    public authentication: AuthenticationService,
    public route: ActivatedRoute,
    private readonly snackBar: MatSnackBar,
    private commonDispatcher: CommonDispatcher,
    private readonly finegrainRoles: FinegrainRolesService,
    private readonly ngZone: NgZone
  ) {
    this.activePortals = finegrainRoles.getSupportedPortals();
    this.authentication.isLoggedIn() ? this.authDispatcher.setLogin(true) : this.authentication.login();
    this.authObservable.subscribe(({ isLoggedIn, name, userAvailableRoles }) => {
      this.isLoggedIn = isLoggedIn;
      this.userName = name;
      this.adminCheck = userAvailableRoles.filter(admin => admin === 'OP_ADMIN' || admin === 'AGERO' || admin === 'LMEX' || admin === '21MM_ADMIN').length > 0;
      if (isLoggedIn && userAvailableRoles.length > 0) {
        this.autoRouteOnLogin();
      }
    });
  }
  /**
   * @function getPermission get Permissions to display portals
   */
  public getPermission(portal: string): boolean {
    return this.finegrainRoles.getPortalPermissions(portal);
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit(): void {
    this.syncActivePortalFromUrl(this.router.url);
    this.idleSubscriptions.push(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe(event => {
        this.syncActivePortalFromUrl(event.urlAfterRedirects);
      })
    );
    this.updateLoginStatus();
    this.getUserDetails();
    this.startIdleTimer();
  }

  /**
   * @function ngOnDestroy cleans up idle timer and event listeners
   */
  ngOnDestroy(): void {
    this.stopIdleTimer();
    this.idleSubscriptions.forEach(sub => sub.unsubscribe());
    this.idleSubscriptions = [];
  }

  /**
   * @method onConClick should dimiss the snack bar
   * @return void
   */
  @HostListener('click')
  public onClick(): void {
    this.snackBar.dismiss();
  }
  /**
   * @arg login to login
   * @returns void
   **/
  public login(): void {
    this.authentication.login();
    this.updateLoginStatus();
  }
  /**
   * @arg updateLoginStatus updates the login status
   * @returns void
   **/
  private updateLoginStatus(): void {
    this.authDispatcher.setLogin(this.authentication.isLoggedIn());
  }
  /**
   * @method getUserDetails gets the user details and sets
   * @returns void
   **/
  private getUserDetails(): void {
    if (this.authentication.isLoggedIn()) {
      this.authentication.setUserDetails();
    }
  }
  /**
   * @method autoRouteOnLogin navigates to the first available portal when the user lands on
   * the bare /dashboard path after login, restoring the pre-MSAL-v2 behaviour of routing
   * directly into TST 2.0 (admin) without requiring a manual tab click.
   */
  private autoRouteOnLogin(): void {
    const urlPath = this.router.url.split('?')[0];
    if (urlPath !== '/dashboard' && urlPath !== '/') {
      return;
    }
    const preferred = ['admin', 'vcm', 'svppv2', 'report', 'user_role'];
    const target = preferred.find(portal => this.finegrainRoles.getPortalPermissions(portal));
    if (target) {
      this.activePortal = target;
      this.router.navigate([`/dashboard/${target}`], { skipLocationChange: false });
    }
  }

  /**
   * @method syncActivePortalFromUrl updates the highlighted nav tab from the current URL.
   * Called on init and on every NavigationEnd so browser Back/Forward keep the underline in sync.
   */
  private syncActivePortalFromUrl(url: string): void {
    const path = (url || '').split('?')[0];
    this.activePortal = this.activePortals.find(
      (name) => path.split('/')[2] === name
    );
  }  

  /**
   * routes back to Dashboard
   */
  public routeHome(): void {
    this.activePortal = '';
    this.router.navigate(['/dashboard'], { skipLocationChange: false });
  }
  /**
   * logouts
   */
  public logout(): void {
    this.authentication.logout();
    this.authDispatcher.setAvailabletype(['']);
  }
  /**
   * @method userGuide Redirects the user to the correct user guide based on user permissions
   */
  public userGuide(): void {
    window.open(environment.admin_guide_url, '_blank');
  }
  /**
   * route to portal components
   */
  public routePortal(portal: string): void {
    this.activePortal = portal;
    if (this.activePortals.includes(portal)) {
      if (portal === 'admin') {
        this.commonDispatcher.resetAll();
      } else if (portal === '21mm_navi') {
        portal = '';
        window.open(environment.navi_portal_url, '_blank');
      }
      this.router.navigate([`/dashboard/${portal}`], {
        queryParams: {},
        skipLocationChange: false,
      });
    }
  }

  /**
   * @method startIdleTimer wires up user-activity listeners, visibilitychange, and the
   * polling interval. Only time spent with THIS tab visible and the user inactive counts
   * toward the 60-minute threshold — other tabs, other apps, and locked screens are excluded.
   */
  private startIdleTimer(): void {
    if (!this.lastActivityTimeExists()) {
      this.lastActivityTime = Date.now();
    }

    this.boundActivityHandler = (_event: Event) => {
      this.lastActivityTime = Date.now();
    };
    this.USER_EVENTS.forEach(ev =>
      document.addEventListener(ev, this.boundActivityHandler, { passive: true })
    );
    this.visibilityChangeHandler = () => { };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    this.storageLogoutHandler = (e: StorageEvent) => {
      if (e.key === this.LOGOUT_BROADCAST_KEY && e.newValue) {
        this.ngZone.run(() => this.authentication.logoutBroadcastReceived());
      }
    };
    window.addEventListener('storage', this.storageLogoutHandler);
    this.ngZone.runOutsideAngular(() => {
      this.idleCheckInterval = setInterval(() => {
        const idleMs = Date.now() - this.lastActivityTime;
        if (idleMs >= this.IDLE_TIMEOUT_MS) {
          this.ngZone.run(() => this.logoutSession());
        }
      }, 10000);
    });
  }

  /**
   * @method stopIdleTimer removes all listeners and clears the polling interval.
   */
  private stopIdleTimer(): void {
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }
    if (this.boundActivityHandler) {
      this.USER_EVENTS.forEach(ev =>
        document.removeEventListener(ev, this.boundActivityHandler)
      );
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    if (this.storageLogoutHandler) {
      window.removeEventListener('storage', this.storageLogoutHandler);
      this.storageLogoutHandler = null;
    }
  }

  /**
   * @method logoutSession stops all timers and triggers logout.
   */
  public logoutSession(): void {
    this.stopIdleTimer();
    this.authDispatcher.setAvailabletype(['']);
    try { localStorage.removeItem(this.IDLE_STORAGE_KEY); } catch (_) { }
    this.authentication.logout();
  }
}
